import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import moment from "moment";
import { useApi } from "../utils/api";
import AddExercise from "./CoachPortal/AddExercise";
import { useAuth } from "../utils/auth";
import TemplateCreationModal from "./TemplateCreationModal";
import TemplateSelectionModal from "./TemplateSelectionModal";
import VideoSelectionModal from "./CoachPortal/VideoSelectionModal";
import CopyWorkoutModal from "./CopyWorkoutModal";
import WorkoutMetaFields from "./CoachPortal/WorkoutMetaFields";
import { workoutDateToUnixSeconds } from "../utils/workoutDate";
import {
  collectExerciseNamesFromFormExercises,
  exerciseNameSetFromStrings,
} from "../utils/exerciseNameMatch";
import { loadExerciseDatabase } from "../utils/exerciseDatabase";
import { normalizeRepsForPayload } from "../utils/repsPayload";
import { normalizeWeightForPayload } from "../utils/weightPayload";

// Helper function to get default exercise type based on position
const getDefaultExerciseType = (index) => {
  if (index === 0) return EXERCISE_TYPES.WARMUP;
  if (index === 1) return EXERCISE_TYPES.MAIN;
  return EXERCISE_TYPES.ACCESSORY;
};

// Primary field list with RPE last (matches set-row column order before actions).
const orderPrimaryWithRpeLast = (primary) => {
  if (!Array.isArray(primary) || primary.length === 0) return primary;
  const upper = (f) =>
    typeof f === "string" ? f.toUpperCase() : String(f).toUpperCase();
  const rpe = primary.find((f) => upper(f) === "RPE");
  const rest = primary.filter((f) => upper(f) !== "RPE");
  return rpe ? [...rest, rpe] : rest;
};

// Normalize exercise-level fields:
// - Allow up to 3 primary fields (REPS, WEIGHT, RPE, TIME, DISTANCE, DEGREE)
// - Allow BACKOFF in addition to those (does not count toward the 3-field limit)
// - Legacy: many workouts stored only WEIGHT/RPE; reps were always implied for strength sets.
// - RPE without WEIGHT still gets a WEIGHT column (empty loads / RPE-only prescriptions).
const normalizeExerciseFields = (fields) => {
  if (!Array.isArray(fields) || fields.length === 0)
    return ["REPS", "WEIGHT", "RPE"];
  const hasBackoff = fields.some(
    (f) => typeof f === "string" && f.toUpperCase() === "BACKOFF"
  );
  let primary = fields.filter(
    (f) => typeof f === "string" && f.toUpperCase() !== "BACKOFF"
  );
  const upper = (f) =>
    typeof f === "string" ? f.toUpperCase() : String(f).toUpperCase();
  let lp = primary.map(upper);
  const hasStrength = lp.includes("WEIGHT") || lp.includes("RPE");
  const isPureTimedOrMeasured =
    lp.includes("TIME") || lp.includes("DISTANCE") || lp.includes("DEGREE");
  if (hasStrength && !isPureTimedOrMeasured && !lp.includes("REPS")) {
    primary = ["REPS", ...primary];
    lp = primary.map(upper);
  }
  if (lp.includes("RPE") && !lp.includes("WEIGHT")) {
    const rpeIdx = primary.findIndex((f) => upper(f) === "RPE");
    if (rpeIdx >= 0) {
      primary = [
        ...primary.slice(0, rpeIdx),
        "WEIGHT",
        ...primary.slice(rpeIdx),
      ];
      lp = primary.map(upper);
    }
  }
  const limitedPrimary = primary.slice(0, 3);
  const orderedPrimary = orderPrimaryWithRpeLast(limitedPrimary);
  return hasBackoff ? [...orderedPrimary, "BACKOFF"] : orderedPrimary;
};

const isSetMetricPresent = (v) => {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "number") return Number.isFinite(v);
  return true;
};

function collectAllSetsFromExerciseLike(exercise) {
  const out = [];
  if (Array.isArray(exercise?.sets)) out.push(...exercise.sets);
  if (Array.isArray(exercise?.circuit)) {
    for (const round of exercise.circuit) {
      if (!Array.isArray(round)) continue;
      for (const ex of round) {
        if (Array.isArray(ex?.sets)) out.push(...ex.sets);
      }
    }
  }
  return out;
}

function aggregateSetMetricsPresence(sets) {
  const acc = {
    reps: false,
    weight: false,
    rpe: false,
    time: false,
    distance: false,
    degree: false,
  };
  if (!Array.isArray(sets)) return acc;
  for (const s of sets) {
    if (!s || typeof s !== "object") continue;
    if (isSetMetricPresent(s.reps)) acc.reps = true;
    if (isSetMetricPresent(s.weight)) acc.weight = true;
    if (isSetMetricPresent(s.rpe)) acc.rpe = true;
    if (isSetMetricPresent(s.time)) acc.time = true;
    if (isSetMetricPresent(s.distance)) acc.distance = true;
    if (isSetMetricPresent(s.degree)) acc.degree = true;
  }
  return acc;
}

/** Infer columns from any set in the block (RPE on set 2+ must still enable WEIGHT + RPE). */
const inferPrimaryFieldsFromAggregatedSets = (exercise) => {
  const present = aggregateSetMetricsPresence(
    collectAllSetsFromExerciseLike(exercise),
  );
  if (
    present.reps &&
    !present.weight &&
    !present.rpe &&
    !present.time &&
    !present.distance &&
    !present.degree
  ) {
    return orderPrimaryWithRpeLast(["REPS", "WEIGHT", "RPE"]);
  }
  const primary = [];
  if (present.reps) primary.push("REPS");
  if (present.weight) primary.push("WEIGHT");
  if (present.rpe) primary.push("RPE");
  if (present.time) primary.push("TIME");
  if (present.distance) primary.push("DISTANCE");
  if (present.degree) primary.push("DEGREE");
  if (present.rpe && !primary.includes("WEIGHT")) {
    const idx = primary.indexOf("RPE");
    if (idx >= 0) primary.splice(idx, 0, "WEIGHT");
  }
  if (primary.length === 0) return ["REPS", "WEIGHT", "RPE"];
  const strength = present.weight || present.rpe;
  const measured = present.time || present.distance || present.degree;
  if (measured && !strength && !present.reps) {
    if (present.time) return orderPrimaryWithRpeLast(["TIME"]);
    if (present.distance) return orderPrimaryWithRpeLast(["DISTANCE"]);
    return orderPrimaryWithRpeLast(["DEGREE"]);
  }
  const upper = primary.map((f) =>
    typeof f === "string" ? f.toUpperCase() : String(f).toUpperCase()
  );
  const hasStrength = upper.includes("WEIGHT") || upper.includes("RPE");
  const isPureTimedOrMeasured =
    upper.includes("TIME") ||
    upper.includes("DISTANCE") ||
    upper.includes("DEGREE");
  let ordered = [...primary];
  if (hasStrength && !isPureTimedOrMeasured && !upper.includes("REPS")) {
    ordered = ["REPS", ...ordered];
  }
  return orderPrimaryWithRpeLast(ordered.slice(0, 3));
};

// Exercise-level fields (max 3 primary + BACKOFF). Empty array = intentional. Old data has type on sets.
const getExerciseFields = (exercise) => {
  const hasBackoffSet =
    (Array.isArray(exercise?.sets)
      ? exercise.sets.some(
          (s) =>
            s &&
            s.backoffPercent !== undefined &&
            s.backoffPercent !== null &&
            s.backoffFromIndex !== undefined &&
            s.backoffFromIndex !== null
        )
      : false) ||
    (Array.isArray(exercise?.circuit)
      ? exercise.circuit.some((round) =>
          Array.isArray(round)
            ? round.some((ex) =>
                Array.isArray(ex?.sets)
                  ? ex.sets.some(
                      (s) =>
                        s &&
                        s.backoffPercent !== undefined &&
                        s.backoffPercent !== null &&
                        s.backoffFromIndex !== undefined &&
                        s.backoffFromIndex !== null
                    )
                  : false
              )
            : false
        )
      : false);

  if (Array.isArray(exercise?.fields)) {
    const normalized = normalizeExerciseFields(exercise.fields);
    if (hasBackoffSet && !normalized.includes("BACKOFF")) {
      return normalizeExerciseFields([...normalized, "BACKOFF"]);
    }
    return normalized;
  }
  // Old data: no fields on exercise, but sets have type/types
  const firstSet =
    exercise?.sets?.[0] || exercise?.circuit?.[0]?.[0]?.sets?.[0];
  const firstSetHasType =
    firstSet &&
    (firstSet.type != null ||
      (Array.isArray(firstSet.types) && firstSet.types.length > 0));
  let baseFields;
  if (firstSetHasType) {
    if (Array.isArray(firstSet.types) && firstSet.types.length > 0) {
      baseFields = orderPrimaryWithRpeLast(firstSet.types.slice(0, 3));
    } else if (firstSet.type === "TIME") {
      baseFields = ["TIME"];
    } else if (firstSet.type === "DEGREE") {
      baseFields = ["DEGREE"];
    } else if (firstSet.type === "RPE") {
      baseFields = ["REPS", "WEIGHT", "RPE"];
    } else {
      baseFields = ["REPS", "WEIGHT", "RPE"];
    }
  } else {
    baseFields = inferPrimaryFieldsFromAggregatedSets(exercise);
  }

  if (hasBackoffSet && !baseFields.includes("BACKOFF")) {
    return normalizeExerciseFields([...baseFields, "BACKOFF"]);
  }
  return orderPrimaryWithRpeLast(baseFields);
};

// Exercise type constants
export const EXERCISE_TYPES = {
  WARMUP: "warmup",
  MAIN: "main",
  ACCESSORY: "accessory",
};

const AddWorkoutDetails = ({
  open,
  handleClose = () => {},
  userId,
  workout: rawWorkout = {},
  onSave: onSaveCallback,
  onWorkoutCopied,
  onWorkoutDeleted,
  onWorkoutUpdated,
  submitButtonId,
  onDirtyChange = () => {},
  mode = "workout",
  onDeleteTemplate,
  onCopyTemplate,
  onDeleteDraft,
  draftTemplateMode = false,
  onOpenHistoryExercise,
  focusExerciseIndex = null,
  openCommentsForExerciseIndex = null,
  onNotificationFocusConsumed = () => {},
  previousWeekExerciseNameSet = null,
  onDraftExerciseNamesChange,
  defaultWeightUnit = "lbs",
}) => {
  const resolvedWeightUnit = defaultWeightUnit === "kg" ? "kg" : "lbs";
  const exerciseScrollRefs = useRef([]);
  const [exerciseCatalog, setExerciseCatalog] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadExerciseDatabase()
      .then((data) => {
        if (!cancelled && data?.exercises?.length) setExerciseCatalog(data);
      })
      .catch(() => {
        if (!cancelled) setExerciseCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    addWorkoutForUser,
    saveWorkoutDetails,
    createWorkoutTemplate,
    deleteWorkout,
    getUserProducts,
    patchUserProduct,
    getContentMetadata,
  } = useApi();
  const [contentLibraryVideos, setContentLibraryVideos] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] =
    useState(false);
  const {
    user: { uid: id },
  } = useAuth();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [editingExerciseIndex, setEditingExerciseIndex] = useState(null);
  const [editingRoundIndex, setEditingRoundIndex] = useState(null);
  const [editingCircuitExerciseIndex, setEditingCircuitExerciseIndex] =
    useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [userProducts, setUserProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [deductSession, setDeductSession] = useState(false);

  const isTemplateMode = mode === "template";

  // Fetch content library once when workout details are open (for video suggestions in exercises)
  useEffect(() => {
    if (!open) return;
    getContentMetadata()
      .then((data) => setContentLibraryVideos(Array.isArray(data) ? data : []))
      .catch(() => setContentLibraryVideos([]));
  }, [open, getContentMetadata]);

  useEffect(() => {
    if (!open || focusExerciseIndex == null) return;
    const id = window.requestAnimationFrame(() => {
      const el = exerciseScrollRefs.current[focusExerciseIndex];
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    const t = window.setTimeout(() => onNotificationFocusConsumed(), 400);
    return () => {
      window.cancelAnimationFrame(id);
      window.clearTimeout(t);
    };
  }, [open, focusExerciseIndex, onNotificationFocusConsumed]);

  useEffect(() => {
    if (!userId || isTemplateMode) {
      setProductsLoading(false);
      return;
    }
    setProductsLoading(true);
    getUserProducts(userId, "purchased")
      .then((products) => {
        const list = products && products.length ? products : [];
        setUserProducts(list);
        // Auto-select a subscription if one exists
        const subscription = list.find((p) => p.recurring);
        if (subscription) setSelectedProductId(subscription.priceId);
      })
      .finally(() => setProductsLoading(false));
  }, [userId, isTemplateMode]);

  const selectedProduct =
    userProducts.find((p) => p.priceId === selectedProductId) || null;

  const [workout, setWorkout] = useState({
    name: "",
    description: "",
    exercises: [],
    completed: false,
    ...rawWorkout, // Spread the provided workout last to override defaults
  });

  // Helper to convert date to moment object
  const convertToMoment = (date) => {
    if (!date) return moment();
    if (moment.isMoment(date)) return date.clone();
    if (typeof date === "number") return moment.unix(date);
    return moment(date);
  };

  // Update workout state when rawWorkout changes
  useEffect(() => {
    setWorkout({
      name: "",
      description: "",
      exercises: [],
      currentWeek: 1,
      programLength: 12,
      completed: false,
      ...rawWorkout,
      date: convertToMoment(rawWorkout?.date),
    });
  }, [rawWorkout]);

  // Memoize the initial values
  const initialValues = useMemo(
    () => ({
      name: workout.name || "",
      description: workout.description || "",
      currentWeek: workout.currentWeek || 1,
      programLength: workout.programLength || 12,
      completed: workout.completed !== undefined ? workout.completed : false,
        exercises:
        workout.exercises?.map((exercise, index) => ({
          ...exercise,
          video: exercise.video || "",
          restTime: exercise.restTime || "",
          weightUnit: exercise.weightUnit || resolvedWeightUnit,
          exerciseType: exercise.exerciseType || "single",
          comments: Array.isArray(exercise.comments) ? exercise.comments : [],
          fields: getExerciseFields(exercise),
          rounds:
            exercise.exerciseType === "circuit"
              ? exercise.circuit && Array.isArray(exercise.circuit)
                ? exercise.circuit.length
                : exercise.rounds || 1
              : undefined,
          circuit:
            exercise.exerciseType === "circuit"
              ? exercise.circuit &&
                Array.isArray(exercise.circuit) &&
                exercise.circuit.length > 0
                ? exercise.circuit.map((round) =>
                    round.map((ex) => ({
                      ...ex,
                      video: ex.video || "",
                      fields: getExerciseFields(ex),
                      sets:
                        ex.sets?.length > 0
                          ? ex.sets.map((s) => ({
                              reps:
                                s.reps !== undefined && s.reps !== null
                                  ? String(s.reps)
                                  : "",
                              weight: s.weight ?? "",
                              rpe: s.rpe ?? "",
                              time: s.time ?? "",
                              timeUnit: s.timeUnit || "sec",
                              distance: s.distance ?? "",
                              degree: s.degree ?? "",
                              video: s.video || "",
                              weightPerSide: s.weightPerSide ?? false,
                              repsPerSide: s.repsPerSide ?? false,
                              backoffPercent: s.backoffPercent ?? "",
                              backoffFromIndex:
                                typeof s.backoffFromIndex === "number"
                                  ? s.backoffFromIndex
                                  : null,
                            }))
                          : [
                              {
                                reps: "",
                                weight: "",
                                rpe: "",
                                time: "",
                                timeUnit: "sec",
                                distance: "",
                                degree: "",
                                weightPerSide: false,
                                repsPerSide: false,
                                backoffPercent: "",
                                backoffFromIndex: null,
                              },
                            ],
                    }))
                  )
                : [
                    // Initialize with empty first round for new circuit exercises
                    [
                      {
                        name: "",
                        description: "",
                        video: "",
                        fields: ["REPS", "WEIGHT", "RPE"],
                        sets: [
                          {
                            reps: "",
                            weight: "",
                            rpe: "",
                            time: "",
                            timeUnit: "sec",
                            distance: "",
                            degree: "",
                            weightPerSide: false,
                            repsPerSide: false,
                            backoffPercent: "",
                            backoffFromIndex: null,
                          },
                        ],
                      },
                    ],
                  ]
              : undefined,
          sets:
            exercise.sets?.map((set) => ({
              reps:
                set.reps !== undefined && set.reps !== null
                  ? String(set.reps)
                  : "",
              weight: set.weight ?? "",
              rpe: set.rpe ?? "",
              time: set.time ?? "",
              timeUnit: set.timeUnit || "sec",
              distance: set.distance ?? "",
              degree: set.degree ?? "",
              video: set.video || "",
              weightPerSide: set.weightPerSide ?? false,
              repsPerSide: set.repsPerSide ?? false,
              backoffPercent: set.backoffPercent ?? "",
              backoffFromIndex:
                typeof set.backoffFromIndex === "number"
                  ? set.backoffFromIndex
                  : null,
            })) || [],
        })) || [],
      date: workout.date || moment(),
    }),
    [workout, resolvedWeightUnit]
  );

  // Notify parent when form dirtiness changes so it can guard closes
  useEffect(() => {
    onDirtyChange(false);
    return () => onDirtyChange(false);
  }, [onDirtyChange]);

  // Function to open the video modal for a specific exercise
  // For circuit exercises: index is the parent exercise index, roundIndex and exerciseIndex identify the circuit exercise
  const handleOpenVideoModal = (
    index,
    roundIndex = null,
    exerciseIndex = null
  ) => {
    setEditingExerciseIndex(index);
    setEditingRoundIndex(roundIndex);
    setEditingCircuitExerciseIndex(exerciseIndex);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setEditingExerciseIndex(null);
    setEditingRoundIndex(null);
    setEditingCircuitExerciseIndex(null);
  };

  // Function passed to modal to handle video selection
  // Uses react-final-form's form instance (accessed via render prop)
  const handleSelectVideo = (form, videoUrl) => {
    if (editingExerciseIndex !== null) {
      // Check if this is a circuit exercise
      if (editingRoundIndex !== null && editingCircuitExerciseIndex !== null) {
        // Update the circuit exercise's video field
        form.change(
          `exercises[${editingExerciseIndex}].circuit[${editingRoundIndex}][${editingCircuitExerciseIndex}].video`,
          videoUrl
        );
      } else {
        // Update the parent exercise's video field
        form.change(`exercises[${editingExerciseIndex}].video`, videoUrl);
      }
      handleCloseVideoModal();
    }
  };

  const onSave = async (v) => {
    console.log("Form values received:", v);
    console.log("Current week value:", v.currentWeek);
    console.log("Program length value:", v.programLength);

    const parseBackoffValues = (set, includeBackoff) => {
      if (!includeBackoff) {
        return { backoffPercent: null, backoffFromIndex: null };
      }
      const backoffPercent =
        set.backoffPercent === "" ||
        set.backoffPercent === null ||
        set.backoffPercent === undefined
          ? null
          : parseFloat(set.backoffPercent);
      const backoffFromIndex =
        set.backoffFromIndex === "" ||
        set.backoffFromIndex === null ||
        set.backoffFromIndex === undefined
          ? null
          : parseInt(set.backoffFromIndex, 10);
      return { backoffPercent, backoffFromIndex };
    };

    const exercisesPayload = (v.exercises || []).map((e) => {
      const exerciseType = e.exerciseType || "single";

      const base = {
        name: e.name,
        // Always send a description field; empty string means "cleared"
        description: e.description ?? "",
        video: e.video,
        restTime: e.restTime ? parseInt(e.restTime) : null,
        weightUnit: e.weightUnit || resolvedWeightUnit,
        distanceUnit:
          Array.isArray(e.fields) && e.fields.includes("DISTANCE")
            ? e.distanceUnit || "m"
            : undefined,
        exerciseType,
        fields: normalizeExerciseFields(
          Array.isArray(e.fields) ? e.fields : ["REPS", "WEIGHT", "RPE"]
        ),
        rounds:
          exerciseType === "circuit"
            ? e.circuit && Array.isArray(e.circuit)
              ? e.circuit.length
              : 1
            : undefined,
        comments: e.comments || [],
      };

      if (exerciseType === "circuit") {
        return {
          ...base,
          circuit:
            e.circuit && Array.isArray(e.circuit) && e.circuit.length > 0
              ? e.circuit.map((round) =>
                  round.map((ex) => {
                    const normalizedFields = normalizeExerciseFields(
                      Array.isArray(ex.fields)
                        ? ex.fields
                        : ["REPS", "WEIGHT", "RPE"]
                    );
                    const includeBackoff =
                      Array.isArray(normalizedFields) &&
                      normalizedFields.includes("BACKOFF");
                    return {
                      name: ex.name,
                      description: ex.description,
                      video: ex.video,
                      fields: normalizedFields,
                      distanceUnit:
                        Array.isArray(ex.fields) && ex.fields.includes("DISTANCE")
                          ? ex.distanceUnit || "m"
                          : undefined,
                      sets: (ex.sets || []).map((s) => {
                        const { backoffPercent, backoffFromIndex } =
                          parseBackoffValues(s, includeBackoff);
                      const isBackoffSet =
                        backoffPercent !== null && backoffFromIndex !== null;
                      const timeUnit = s.timeUnit || "sec";
                      const rawTime =
                        s.time === "" || s.time === null || s.time === undefined
                          ? null
                          : parseInt(s.time, 10);

                      return {
                        reps: normalizeRepsForPayload(s.reps),
                        weight: isBackoffSet
                          ? null
                          : normalizeWeightForPayload(s.weight),
                        rpe: s.rpe ? parseInt(s.rpe) : null,
                        time:
                          rawTime === null
                            ? null
                            : rawTime,
                        timeUnit,
                        distance:
                          s.distance != null && s.distance !== ""
                            ? parseFloat(s.distance)
                            : null,
                        degree:
                          s.degree != null && s.degree !== ""
                            ? parseFloat(s.degree)
                            : null,
                        video: s.video || undefined,
                        weightPerSide: s.weightPerSide ?? false,
                        repsPerSide: s.repsPerSide ?? false,
                        backoffPercent,
                        backoffFromIndex,
                      };
                    }),
                    };
                  })
                )
              : [],
          sets: [],
        };
      }

      return {
        ...base,
        circuit: undefined,
        sets: (e.sets || []).map((s) => {
          const includeBackoff =
            Array.isArray(base.fields) && base.fields.includes("BACKOFF");
          const { backoffPercent, backoffFromIndex } = parseBackoffValues(
            s,
            includeBackoff
          );
          const isBackoffSet =
            backoffPercent !== null && backoffFromIndex !== null;

          const timeUnit = s.timeUnit || "sec";
          const rawTime =
            s.time === "" || s.time === null || s.time === undefined
              ? null
              : parseInt(s.time, 10);

          return {
            reps: normalizeRepsForPayload(s.reps),
            weight: isBackoffSet
              ? null
              : normalizeWeightForPayload(s.weight),
            rpe:
              s.rpe === "" || s.rpe === null || s.rpe === undefined
                ? null
                : parseInt(s.rpe),
            time:
              rawTime === null
                ? null
                : rawTime,
            timeUnit,
            distance:
              s.distance != null && s.distance !== ""
                ? parseFloat(s.distance)
                : null,
            degree:
              s.degree != null && s.degree !== "" ? parseFloat(s.degree) : null,
            video: s.video || undefined,
            weightPerSide: s.weightPerSide ?? false,
            repsPerSide: s.repsPerSide ?? false,
            backoffPercent,
            backoffFromIndex,
          };
        }),
      };
    });

    let payload = {
      ...v,
      currentWeek: v.currentWeek ? parseInt(v.currentWeek) : 1,
      programLength: v.programLength ? parseInt(v.programLength) : 12,
      completed:
        v.completed !== undefined ? v.completed : workout.completed || false, // Preserve completed status when patching
      exercises: exercisesPayload,
      date: workoutDateToUnixSeconds(v.date),
    };

    // When editing templates we don't need date/program metadata; strip extras
    if (isTemplateMode) {
      payload = {
        name: payload.name,
        description: payload.description,
        exercises:
          payload.exercises?.map((exercise) => {
            // Use the entire exercise object to ensure we get all fields
            const exName = exercise.name;
            // Preserve explicit empty string so clearing the description works
            const exDesc = exercise.description ?? "";
            const video = exercise.video;
            const restTime = exercise.restTime;
            const weightUnit = exercise.weightUnit;
            const exerciseType = exercise.exerciseType || "single";
            const sets = exercise.sets || [];
            // Calculate rounds from circuit array length
            const rounds =
              exercise.circuit && Array.isArray(exercise.circuit)
                ? exercise.circuit.length
                : exercise.rounds || 1;
            // Get circuit from the exercise object - it should already be processed from form values
            const circuit = exercise.circuit;
            const comments = exercise.comments || [];

            const exerciseObj = {
              name: exName,
              description: exDesc,
              video,
              restTime,
              weightUnit,
              exerciseType: exerciseType || "single",
              comments: comments || [],
            };
            if (
              Array.isArray(exercise.fields) &&
              exercise.fields.includes("DISTANCE") &&
              exercise.distanceUnit
            ) {
              exerciseObj.distanceUnit = exercise.distanceUnit;
            }

            // Handle circuit exercises
            if (exerciseType === "circuit") {
              exerciseObj.rounds = rounds;
              exerciseObj.sets = [];
              exerciseObj.fields = normalizeExerciseFields(
                Array.isArray(exercise.fields)
                  ? exercise.fields
                  : ["REPS", "WEIGHT", "RPE"]
              );

              if (circuit && Array.isArray(circuit) && circuit.length > 0) {
                exerciseObj.circuit = circuit.map((round) =>
                  round.map((ex) => {
                    const normalizedFields = normalizeExerciseFields(
                      Array.isArray(ex.fields)
                        ? ex.fields
                        : ["REPS", "WEIGHT", "RPE"]
                    );
                    const includeBackoff =
                      Array.isArray(normalizedFields) &&
                      normalizedFields.includes("BACKOFF");
                    const exObj = {
                      name: ex.name,
                      description: ex.description,
                      video: ex.video,
                      fields: normalizedFields,
                      sets: (ex.sets || []).map((s) => {
                        const timeUnit = s.timeUnit || "sec";
                        const rawTime =
                          s.time === "" ||
                          s.time === null ||
                          s.time === undefined
                            ? null
                            : parseInt(s.time, 10);
                        const { backoffPercent, backoffFromIndex } =
                          parseBackoffValues(s, includeBackoff);

                        return {
                          reps: normalizeRepsForPayload(s.reps),
                          weight: normalizeWeightForPayload(s.weight),
                          rpe: s.rpe ? parseInt(s.rpe) : null,
                          time:
                            rawTime === null
                              ? null
                              : rawTime,
                          timeUnit,
                          distance:
                            s.distance != null && s.distance !== ""
                              ? parseFloat(s.distance)
                              : null,
                          degree:
                            s.degree != null && s.degree !== ""
                              ? parseFloat(s.degree)
                              : null,
                          video: s.video || undefined,
                          weightPerSide: s.weightPerSide ?? false,
                          repsPerSide: s.repsPerSide ?? false,
                          backoffPercent,
                          backoffFromIndex,
                        };
                      }),
                    };
                    if (
                      Array.isArray(ex.fields) &&
                      ex.fields.includes("DISTANCE") &&
                      ex.distanceUnit
                    ) {
                      exObj.distanceUnit = ex.distanceUnit;
                    }
                    return exObj;
                  })
                );
              } else {
                exerciseObj.circuit = [];
              }
            } else {
              exerciseObj.fields = normalizeExerciseFields(
                Array.isArray(exercise.fields)
                  ? exercise.fields
                  : ["REPS", "WEIGHT", "RPE"]
              );
              exerciseObj.sets = sets.map(
                ({
                  reps,
                  weight,
                  rpe,
                  time,
                  distance,
                  degree,
                  weightPerSide,
                  repsPerSide,
                  video: setVideo,
                  backoffPercent,
                  backoffFromIndex,
                  timeUnit,
                }) => {
                  const includeBackoff =
                    Array.isArray(exerciseObj.fields) &&
                    exerciseObj.fields.includes("BACKOFF");
                  const {
                    backoffPercent: parsedBackoffPercent,
                    backoffFromIndex: parsedBackoffFromIndex,
                  } = parseBackoffValues(
                    { backoffPercent, backoffFromIndex },
                    includeBackoff
                  );
                  const isBackoffSet =
                    parsedBackoffPercent !== null &&
                    parsedBackoffFromIndex !== null;
                  const unit = timeUnit || "sec";
                  const rawTime =
                    time === "" || time === null || time === undefined
                      ? null
                      : parseInt(time, 10);

                  return {
                    reps: normalizeRepsForPayload(reps),
                    weight: isBackoffSet
                      ? null
                      : normalizeWeightForPayload(weight),
                    rpe: rpe ? parseInt(rpe) : null,
                    time:
                      rawTime === null
                        ? null
                        : rawTime,
                    timeUnit: unit,
                    distance:
                      distance != null && distance !== ""
                        ? parseFloat(distance)
                        : null,
                    degree:
                      degree != null && degree !== ""
                        ? parseFloat(degree)
                        : null,
                    video: setVideo || undefined,
                    weightPerSide: weightPerSide ?? false,
                    repsPerSide: repsPerSide ?? false,
                    backoffPercent: parsedBackoffPercent,
                    backoffFromIndex: parsedBackoffFromIndex,
                  };
                }
              );
            }

            return exerciseObj;
          }) || [],
      };
    }

    console.log("Final payload:", payload);

    if (onSaveCallback) {
      await onSaveCallback(payload);
    } else if (workout.id) {
      await saveWorkoutDetails(workout.id, payload);
    } else {
      await addWorkoutForUser(userId, payload);
    }

    if (
      deductSession &&
      selectedProduct &&
      !selectedProduct.recurring &&
      selectedProduct.quantityLeft > 0
    ) {
      await patchUserProduct({
        priceId: selectedProduct.priceId,
        quantityLeft: selectedProduct.quantityLeft - 1,
        userId,
        stripeAccountId: selectedProduct.stripeAccountId,
      });
    }

    handleClose();
  };

  const handleCreateTemplate = async (templateName) => {
    const templatePayload = {
      coachId: id,
      workoutInfo: {
        name: templateName,
        description: workout.description,
        exercises: workout.exercises,
      },
    };

    await createWorkoutTemplate(templatePayload);
    setTemplateModalOpen(false);
  };

  const handleUseTemplate = (template) => {
    // Populate the workout with the selected template's details
    const exercisesWithTypes = template.exercises.map((exercise, index) => ({
      ...exercise,
      exerciseType: exercise.exerciseType || getDefaultExerciseType(index),
    }));

    setWorkout({
      ...workout,
      name: template.name,
      description: template.description,
      currentWeek: template.currentWeek || 1,
      programLength: template.programLength || 12,
      exercises: exercisesWithTypes,
    });
    setTemplateSelectionModalOpen(false); // Close the modal after selection
  };

  const handleCopyWorkout = async (selectedDate) => {
    try {
      // Create a copy of the current workout with the new date
      const copiedWorkout = {
        ...workout,
        name: `${workout.name} (Copy)`,
        description: workout.description,
        currentWeek: workout.currentWeek || 1,
        programLength: workout.programLength || 12,
        completed: false, // Mark workout as non-complete
        exercises: workout.exercises.map((exercise) => {
          const newExercise = { ...exercise };
          const fields = getExerciseFields(exercise);
          newExercise.fields = fields;
          // Clear values based on exercise fields
          newExercise.sets = (exercise.sets || []).map((set) => {
            const newSet = { ...set };
            if (!fields.includes("WEIGHT")) delete newSet.weight;
            if (!fields.includes("RPE")) delete newSet.rpe;
            if (!fields.includes("TIME")) delete newSet.time;
            if (!fields.includes("DISTANCE")) delete newSet.distance;
            newSet.video = null;
            newSet.completed = false;
            return newSet;
          });
          if (exercise.exerciseType === "circuit" && exercise.circuit) {
            newExercise.circuit = exercise.circuit.map((round) =>
              round.map((ex) => {
                const newEx = { ...ex };
                const exFields = getExerciseFields(ex);
                newEx.fields = exFields;
                newEx.sets = (ex.sets || []).map((set) => {
                  const newSet = { ...set };
                  if (!exFields.includes("WEIGHT")) delete newSet.weight;
                  if (!exFields.includes("RPE")) delete newSet.rpe;
                  if (!exFields.includes("TIME")) delete newSet.time;
                  if (!exFields.includes("DISTANCE")) delete newSet.distance;
                  newSet.video = null;
                  newSet.completed = false;
                  return newSet;
                });
                return newEx;
              })
            );
          }
          newExercise.exerciseType = exercise.exerciseType;
          newExercise.comments = [];
          return newExercise;
        }),
        date: selectedDate.unix(),
      };

      // Add the copied workout for the user
      await addWorkoutForUser(userId, copiedWorkout);

      // Close the modal
      setCopyModalOpen(false);

      // Call the callback to refresh workouts if provided
      if (onWorkoutCopied) {
        onWorkoutCopied();
      }

      // Close the drawer if we're in inline mode
      if (handleClose) {
        handleClose();
      }
    } catch (error) {
      console.error("Error copying workout:", error);
      alert("Failed to copy workout. Please try again.");
    }
  };

  const handleDeleteWorkout = async () => {
    try {
      await deleteWorkout(workout.id);

      // Call the callback to refresh workouts if provided
      if (onWorkoutDeleted) {
        onWorkoutDeleted();
      }

      // Close the panel
      if (handleClose) {
        handleClose();
      }
    } catch (error) {
      console.error("Error deleting workout:", error);
      alert("Failed to delete workout. Please try again.");
    }
  };

  const handleDeleteTemplate = async () => {
    if (onDeleteTemplate && workout.id) {
      await onDeleteTemplate(workout.id);
    }
  };

  const handleToggleComplete = async () => {
    try {
      const newCompletedStatus = !workout.completed;

      // Update only the completion status via API
      await saveWorkoutDetails(workout.id, {
        completed: newCompletedStatus,
      });

      // Update local state
      setWorkout((prev) => ({
        ...prev,
        completed: newCompletedStatus,
      }));

      onWorkoutUpdated?.();
      handleClose();
    } catch (error) {
      console.error("Error updating workout completion:", error);
      alert("Failed to update workout completion. Please try again.");
    }
  };

  return (
    <>
      <TemplateSelectionModal
        open={templateSelectionModalOpen}
        onClose={() => setTemplateSelectionModalOpen(false)}
        onUseTemplate={handleUseTemplate}
      />
      <TemplateCreationModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onCreate={handleCreateTemplate}
      />
      <CopyWorkoutModal
        open={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        onCopy={handleCopyWorkout}
        workout={workout}
      />
      <Form
        key={workout?.id || "new-workout-form"}
        onSubmit={onSave}
        subscription={{ submitting: true, pristine: true, values: true }}
        mutators={{
          ...arrayMutators,
        }}
        initialValues={initialValues}
        keepDirtyOnReinitialize
        render={({ handleSubmit, form, values, submitting }) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              {typeof onDraftExerciseNamesChange === "function" && (
                <FormSpy
                  subscription={{ values: true }}
                  onChange={(formState) => {
                    const names = collectExerciseNamesFromFormExercises(
                      formState?.values?.exercises
                    );
                    onDraftExerciseNamesChange(
                      exerciseNameSetFromStrings(names)
                    );
                  }}
                />
              )}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "white",
                  p: 2,
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                }}
              >
                <FormSpy
                  subscription={{ pristine: true }}
                  onChange={({ pristine: currentPristine }) =>
                    onDirtyChange(!currentPristine)
                  }
                />
                {isTemplateMode ? (
                  <>
                    {onCopyTemplate && workout.id && (
                      <Button
                        variant="outlined"
                        sx={{ mr: 2 }}
                        onClick={onCopyTemplate}
                      >
                        Copy
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      sx={{ mr: 2 }}
                      onClick={handleDeleteTemplate}
                      disabled={!workout.id}
                    >
                      Delete Template
                    </Button>
                  </>
                ) : workout.id ? (
                  <>
                    <Button
                      variant="outlined"
                      sx={{ mr: 2 }}
                      onClick={() => setTemplateModalOpen(true)}
                    >
                      Create Template
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ mr: 2 }}
                      onClick={() => setCopyModalOpen(true)}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ mr: 2 }}
                      onClick={handleDeleteWorkout}
                    >
                      Delete
                    </Button>
                    <Button
                      variant={workout.completed ? "contained" : "outlined"}
                      color={workout.completed ? "success" : "primary"}
                      sx={{ mr: 2 }}
                      onClick={handleToggleComplete}
                    >
                      {workout.completed ? "Uncomplete" : "Complete"}
                    </Button>
                  </>
                ) : (
                  <>
                    {draftTemplateMode ? (
                      <Button
                        variant="outlined"
                        sx={{ mr: 2 }}
                        onClick={() => setTemplateModalOpen(true)}
                      >
                        Create Template
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={{ mr: 2 }}
                        onClick={() => setTemplateSelectionModalOpen(true)}
                      >
                        Use Template
                      </Button>
                    )}
                    {onDeleteDraft && (
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ mr: 2 }}
                        onClick={onDeleteDraft}
                      >
                        Remove
                      </Button>
                    )}
                  </>
                )}
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  id={submitButtonId}
                  loading={submitting}
                >
                  Save
                </LoadingButton>
              </Box>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "white",
                  minHeight: "100%",
                }}
              >
                  {!isTemplateMode && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        Purchased Product
                      </Typography>
                      {productsLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Loading products...
                          </Typography>
                        </Box>
                      ) : userProducts.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel>Select Product</InputLabel>
                            <Select
                              value={selectedProductId}
                              label="Select Product"
                              onChange={(e) => {
                                setSelectedProductId(e.target.value);
                                setDeductSession(false);
                              }}
                            >
                              <MenuItem value="">None</MenuItem>
                              {userProducts.map((p) => (
                                <MenuItem key={p.priceId} value={p.priceId}>
                                  {p.productName}
                                  {p.recurring
                                    ? " (Subscription)"
                                    : ` — ${p.quantityLeft ?? 0} session${
                                        p.quantityLeft !== 1 ? "s" : ""
                                      } left`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {selectedProduct && selectedProduct.recurring && (
                            <Chip
                              label="Subscription"
                              size="small"
                              color="primary"
                            />
                          )}
                          {selectedProduct && !selectedProduct.recurring && (
                            <>
                              <Chip
                                label={`${
                                  selectedProduct.quantityLeft ?? 0
                                } session${
                                  selectedProduct.quantityLeft !== 1 ? "s" : ""
                                } remaining`}
                                size="small"
                                color={
                                  (selectedProduct.quantityLeft ?? 0) > 0
                                    ? "success"
                                    : "error"
                                }
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={deductSession}
                                    onChange={(e) =>
                                      setDeductSession(e.target.checked)
                                    }
                                    size="small"
                                    disabled={
                                      (selectedProduct.quantityLeft ?? 0) <= 0
                                    }
                                  />
                                }
                                label="Deduct 1 session on save"
                                sx={{ m: 0 }}
                              />
                            </>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No purchased products
                        </Typography>
                      )}
                    </Box>
                  )}
                  <WorkoutMetaFields
                    form={form}
                    showDate={!isTemplateMode}
                    showProgramFields={!isTemplateMode}
                  />

                  <FieldArray
                    name="exercises"
                    subscription={{ value: true }}
                    render={({ fields }) => (
                      <>
                        {fields.map((name, i) => (
                          <Box
                            key={name}
                            sx={{ mt: 2 }}
                            ref={(el) => {
                              exerciseScrollRefs.current[i] = el;
                            }}
                          >
                            <AddExercise
                              index={i}
                              removeExercise={() => fields.remove(i)}
                              onOpenVideoModal={handleOpenVideoModal}
                              onMoveUp={() => fields.move(i, i - 1)}
                              onMoveDown={() => fields.move(i, i + 1)}
                              canMoveUp={i > 0}
                              canMoveDown={i < fields.length - 1}
                              contentLibraryVideos={contentLibraryVideos}
                              userId={userId}
                              workoutId={workout?.id}
                              expandCommentsSection={
                                openCommentsForExerciseIndex === i
                              }
                              onOpenHistory={(exerciseName) =>
                                onOpenHistoryExercise?.(exerciseName)
                              }
                              previousWeekExerciseNameSet={
                                previousWeekExerciseNameSet
                              }
                              exerciseCatalog={exerciseCatalog}
                            />
                          </Box>
                        ))}
                        <Button
                          size="small"
                          sx={{ mt: 1, mb: 1 }}
                          onClick={() => {
                            const exerciseIndex = fields.length;
                            fields.push({
                              sets: [
                                {
                                  reps: "",
                                  weight: "",
                                  rpe: "",
                                  time: "",
                                  timeUnit: "sec",
                                  distance: "",
                                  degree: "",
                                  weightPerSide: false,
                                  repsPerSide: false,
                                  backoffPercent: "",
                                  backoffFromIndex: null,
                                },
                              ],
                              weightUnit: resolvedWeightUnit,
                              exerciseType: "single",
                              rounds: 1,
                              fields: ["REPS", "WEIGHT", "RPE"],
                              comments: [],
                            });
                          }}
                        >
                          Add Exercise
                        </Button>
                      </>
                    )}
                  />
                  <VideoSelectionModal
                    open={videoModalOpen}
                    onClose={handleCloseVideoModal}
                    onSelectVideo={(videoUrl) =>
                      handleSelectVideo(form, videoUrl)
                    }
                  />
              </Box>
            </Box>
          );
        }}
      />
    </>
  );
};

export default React.memo(AddWorkoutDetails);
